import React, { useMemo, useRef } from 'react';
import { Plus16, Tag } from 'shared/assets/icons';
import { TagContextMenu } from 'shared/components/connected/tag-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { IconButton, Sizes } from 'shared/components/ui/icon-button';
import {
  SortableContainer,
  SortableContainerProps,
} from 'shared/components/ui/sortable-container';
import { useOpenCreateTask } from 'shared/contexts/task-detail';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { Paths } from 'shared/routes';
import { DraggableOptions } from 'shared/types/draggable-types';
import { Tag as TagType } from 'shared/types/tag';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { useTheme } from 'styled-components';

import * as Styled from './tag-side-menu-item.style';

export type TagSideMenuItemProps = {
  tag: TagType;
  currentPath: string;
  isMoving?: boolean;
  hasDnd?: boolean;
  children?: React.ReactNode;
};

export const TagSideMenuItem: React.FC<TagSideMenuItemProps> = ({
  tag,
  currentPath,
  isMoving,
  hasDnd,
  children,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLAnchorElement>(null);

  const { position, open, close } = useContextMenuState({
    disabled: theme.isMobile,
  });

  const openCreateTask = useOpenCreateTask();

  const onCreateTask = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openCreateTask({ tagIds: [tag.id] });
  };

  const url = replaceUrlParams(Paths.TagDetail, { id: tag.id });
  const isActive = !!currentPath && currentPath.includes(url);

  const Container = hasDnd ? SortableContainer : React.Fragment;

  const containerProps = useMemo(
    () =>
      (hasDnd
        ? { id: tag.id, type: DraggableOptions.Tag }
        : {}) as SortableContainerProps,
    [hasDnd, tag.id],
  );

  return (
    <Container {...containerProps}>
      <Styled.Link
        to={url}
        $active={isActive}
        $isMoving={!!isMoving}
        ref={containerRef}
        onContextMenu={open}
      >
        <Styled.IconContainer $color={tag.color}>
          <Icon icon={Tag} />
        </Styled.IconContainer>
        <Styled.Title>{tag.name}</Styled.Title>
        {children}
        <Styled.IconButtonContainer>
          <IconButton onClick={onCreateTask} icon={Plus16} size={Sizes.Small} />
        </Styled.IconButtonContainer>

        {!!position && (
          <TagContextMenu id={tag.id} onClose={close} location={position} />
        )}
      </Styled.Link>
    </Container>
  );
};
